import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import {
  COMMAND_PRIORITY_HIGH,
  type EditorState,
  INSERT_PARAGRAPH_COMMAND,
  KEY_ENTER_COMMAND,
  type LexicalEditor,
  PASTE_COMMAND,
  SKIP_DOM_SELECTION_TAG,
} from 'lexical'
import { useCallback, useEffect, useRef } from 'react'

import {
  $exportDocChatPrompt,
  $importDocChatPrompt,
  $pasteDocChatPrompt,
} from './docChatPromptMarkdown'

const EXTERNAL_VALUE_TAG = 'docchat-external-value'

export function DocChatPromptEditorPlugin({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  const [editor] = useLexicalComposerContext()
  const lastValue = useRef(value)

  useEffect(() => {
    if (value === lastValue.current) return
    lastValue.current = value
    editor.update(() => $importDocChatPrompt(value), {
      tag: [EXTERNAL_VALUE_TAG, SKIP_DOM_SELECTION_TAG],
    })
  }, [editor, value])

  useEffect(
    () =>
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          if (!event?.shiftKey || event.isComposing || editor.isComposing())
            return false
          event.preventDefault()
          return editor.dispatchCommand(INSERT_PARAGRAPH_COMMAND, undefined)
        },
        COMMAND_PRIORITY_HIGH
      ),
    [editor]
  )

  useEffect(
    () =>
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          if (!(event instanceof ClipboardEvent)) return false
          const data = event.clipboardData
          if (!data || data.files.length || data.getData('text/html'))
            return false
          const text = data.getData('text/plain')
          if (!text) return false
          event.preventDefault()
          $pasteDocChatPrompt(text)
          return true
        },
        COMMAND_PRIORITY_HIGH
      ),
    [editor]
  )

  const handleChange = useCallback(
    (editorState: EditorState, _editor: LexicalEditor, tags: Set<string>) => {
      if (tags.has(EXTERNAL_VALUE_TAG)) return
      const nextValue = editorState.read($exportDocChatPrompt)
      if (nextValue === lastValue.current) return
      lastValue.current = nextValue
      onChange(nextValue)
    },
    [onChange]
  )

  return <OnChangePlugin ignoreSelectionChange onChange={handleChange} />
}
